import { useTranslation } from 'react-i18next';
import { useDirection } from '../../../contexts/DirectionContext';
import { Topic } from '../../../types/topic';
import { SearchFilters, SearchType } from '../../../types/search';
import { FilterSection } from './components/FilterSection';
import { TopicsSection } from './components/TopicsSection';
import { SortSection } from './components/SortSection';
import { DurationSection } from './components/DurationSection';
import { BroadcastStatusSection } from './components/BroadcastStatusSection';
import { filterConfigs } from './config';
import { useFilterMenu } from './useFilterMenu';
import { useSelectedValues } from './useSelectedValues';

interface FilterMenuProps {
  contentType: SearchType;
  filters: SearchFilters;
  topics: Topic[];
  onChange: (filters: SearchFilters) => void;
  onClose: () => void;
  className?: string;
}

export const FilterMenu = ({
  contentType,
  filters,
  topics,
  onChange,
  onClose,
  className = ''
}: FilterMenuProps) => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const {
    draftFilters,
    sectionsCollapsed,
    topicSearch,
    numTopicsShown,
    filteredTopics,
    hasMoreTopics,
    hasLessTopics,
    handleTopicToggle,
    handleDurationToggle,
    handleSortChange,
    handleSortOrderChange,
    handleBroadcastStatusToggle,
    handleShowMore,
    handleShowLess,
    setTopicSearch,
    toggleSection
  } = useFilterMenu({ filters, topics, onChange });

  const renderFilterSection = (configId: string) => {
    const config = filterConfigs.find(c => c.id === configId);
    if (!config || (config.isVisible && !config.isVisible(contentType))) return null;

    const selectedValues = useSelectedValues(config, draftFilters, t, topics);

    const getContent = () => {
      switch (configId) {
        case 'topics':
          return (
            <TopicsSection
              topicSearch={topicSearch}
              setTopicSearch={setTopicSearch}
              filteredTopics={filteredTopics}
              numTopicsShown={numTopicsShown}
              draftFilters={draftFilters}
              hasMoreTopics={hasMoreTopics}
              hasLessTopics={hasLessTopics}
              onTopicToggle={handleTopicToggle}
              onShowMore={handleShowMore}
              onShowLess={handleShowLess}
              t={t}
            />
          );
        case 'sort':
          return (
            <SortSection
              contentType={contentType}
              draftFilters={draftFilters}
              onSortChange={handleSortChange}
              onSortOrderChange={handleSortOrderChange}
              t={t}
            />
          );
        case 'duration':
          return (
            <DurationSection
              draftFilters={draftFilters}
              onDurationToggle={handleDurationToggle}
              t={t}
            />
          );
        case 'broadcastStatus':
          return (
            <BroadcastStatusSection
              draftFilters={draftFilters}
              onBroadcastStatusToggle={handleBroadcastStatusToggle}
              t={t}
            />
          );
        default:
          return null;
      }
    };

    return (
      <FilterSection
        key={config.id}
        title={t(config.titleKey)}
        isCollapsed={sectionsCollapsed[config.id]}
        onToggle={() => toggleSection(config.id)}
        selectedValues={selectedValues}
      >
        {getContent()}
      </FilterSection>
    );
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="space-y-6">
          {filterConfigs.map(config => renderFilterSection(config.id))}
        </div>
      </div>
    </div>
  );
}; 